/**
 * 循环容器节点组件 (Subflow/Group)
 *
 * 使用 React Flow 的 subflows 机制实现循环容器
 * - 子节点通过 parentId 指向循环容器
 * - 子节点使用 extent: 'parent' 限制在容器内
 * - 支持拖拽调整容器大小
 * 
 * Features:
 * - 章节范围配置
 * - 迭代步数配置
 * - 循环反馈开关
 * - 实时迭代进度显示（运行时）
 * - 子节点容器
 */

import React, { memo, useMemo } from 'react';
import { clsx } from 'clsx';
import type { NodeProps } from '@xyflow/react';
import { NodeResizeControl } from '@xyflow/react';
import type { LoopNodeData } from '../../../types/workflow';
import { useNodeExecutionStatus } from '../../../hooks/useNodeExecutionStatus';
import { useExecutionStore, useWorkflowStore } from '../../../store/workflowStore';

/**
 * 循环进度条组件
 */
const LoopProgressBar: React.FC<{
  current: number;
  total: number;
}> = memo(({ current, total }) => {
  const progress = total > 0 ? (current / total) * 100 : 0;

  return (
    <div className="w-full h-1.5 bg-purple-200/50 rounded-full overflow-hidden">
      <div
        className="h-full bg-purple-500 rounded-full transition-all duration-150"
        style={{ width: `${progress}%` }}
      />
    </div>
  );
});

LoopProgressBar.displayName = 'LoopProgressBar';

/**
 * 迭代状态徽章
 */
const IterationBadge: React.FC<{
  current: number;
  total: number;
  currentChapter: number;
}> = memo(({ current, total, currentChapter }) => {
  return (
    <div className="flex items-center gap-2 flex-wrap">
      {/* 迭代进度 */}
      <div className="flex items-center gap-1 text-xs text-purple-600 bg-purple-100/80 px-2 py-0.5 rounded animate-loop-iteration">
        <svg className="w-3 h-3 animate-spin" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
        </svg>
        <span className="font-medium">第 {current + 1}/{total} 轮</span>
      </div>
      
      {/* 当前章节 */}
      <div className="flex items-center gap-1 text-xs text-purple-700 bg-purple-200/80 px-2 py-0.5 rounded">
        <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
        </svg>
        <span>章节 {currentChapter}</span>
      </div>
    </div>
  );
});

IterationBadge.displayName = 'IterationBadge';

/**
 * 子节点占位提示
 */
const EmptyChildrenPlaceholder: React.FC = memo(() => (
  <div className="flex-1 flex flex-col items-center justify-center text-gray-400 py-8">
    <svg className="w-8 h-8 mb-2 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
    </svg>
    <span className="text-xs text-center">
      拖拽节点到此处<br />添加循环内容
    </span>
  </div>
));

EmptyChildrenPlaceholder.displayName = 'EmptyChildrenPlaceholder';

/**
 * 缩放控制组件
 */
const ResizeControl: React.FC = memo(() => (
  <NodeResizeControl
    style={{
      background: 'transparent',
      border: 'none',
    }}
    minWidth={300}
    minHeight={200}
  >
    <svg
      className="absolute bottom-1 right-1 w-4 h-4 text-gray-400 hover:text-gray-600 transition-colors"
      viewBox="0 0 24 24"
      fill="currentColor"
    >
      <path d="M22 22H20V20H22V22ZM22 18H20V16H22V18ZM18 22H16V20H18V22ZM22 14H20V12H22V14ZM18 18H16V16H18V18ZM14 22H12V20H14V22Z" />
    </svg>
  </NodeResizeControl>
));

ResizeControl.displayName = 'ResizeControl';

/**
 * 循环容器节点主组件 (Subflow/Group)
 */
const LoopNodeComponent: React.FC<NodeProps> = ({
  id,
  data,
  selected,
}) => {
  // 类型断言
  const nodeData = data as unknown as LoopNodeData;

  // 使用统一的执行状态 Hook
  const {
    executionStatus,
    isRunning,
    isCompleted,
    isFailed,
  } = useNodeExecutionStatus(id);

  // 获取循环上下文
  const loopContext = useExecutionStore((state) => state.context.loop);

  // 获取所有节点，找出子节点和当前节点的 style
  const allNodes = useWorkflowStore((state) => state.nodes);
  const currentNode = useMemo(() => allNodes.find((n) => n.id === id), [allNodes, id]);
  const childNodes = useMemo(() => {
    return allNodes.filter((n) => n.parentId === id);
  }, [allNodes, id]);

  // 计算循环次数
  const totalChapters = nodeData.chapter_end - nodeData.chapter_start + 1;
  const step = nodeData.step || 1;
  const totalIterations = Math.ceil(totalChapters / step);

  // 当前循环进度
  const currentIteration = loopContext?.iteration_index ?? 0;
  const currentChapter = loopContext?.current_chapter ?? nodeData.chapter_start;

  // 是否显示迭代进度
  const showIterationProgress = isRunning && loopContext;

  // 容器样式（使用节点的 style 或默认值）
  const containerStyle = useMemo(() => ({
    width: currentNode?.style?.width ?? '100%',
    height: currentNode?.style?.height ?? '100%',
    minWidth: '300px',
    minHeight: '200px',
  }), [currentNode?.style]);

  // 容器类名
  const containerClassName = useMemo(() => {
    return clsx(
      'relative w-full h-full rounded-lg border-2 transition-all duration-150',
      // 背景
      'bg-purple-50/30',
      // 边框
      selected ? 'border-purple-400 shadow-lg shadow-purple-200/50' : 'border-purple-200 hover:border-purple-300',
      // 执行状态
      isRunning && 'border-purple-500 animate-pulse-subtle',
      isCompleted && 'border-green-400',
      isFailed && 'border-red-400',
    );
  }, [selected, isRunning, isCompleted, isFailed]);

  return (
    <div
      className={containerClassName}
      style={containerStyle}
      data-node-id={id}
      data-node-type="loop"
      data-execution-status={executionStatus}
    >
      {/* 节点头部 - 固定在顶部 */}
      <div className={clsx(
        'sticky top-0 z-10 px-3 py-2',
        'bg-gradient-to-r from-purple-100 to-purple-50',
        'border-b border-purple-200/50 rounded-t-lg',
        selected && 'bg-gradient-to-r from-purple-200 to-purple-100',
      )}>
        <div className="flex items-center gap-2">
          {/* 循环图标 */}
          <svg className="w-4 h-4 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
          </svg>
          
          {/* 标题 */}
          <span className="text-sm font-medium text-purple-700 flex-1 truncate">
            {nodeData.label || '循环容器'}
          </span>
          
          {/* 章节范围徽章 */}
          <span className="text-xs bg-purple-200/80 text-purple-700 px-2 py-0.5 rounded">
            章节 {nodeData.chapter_start}-{nodeData.chapter_end}
          </span>
          
          {/* 迭代次数徽章 */}
          <span className="text-xs bg-purple-100 text-purple-600 px-2 py-0.5 rounded">
            {totalIterations} 轮
          </span>
        </div>

        {/* 执行进度（运行中显示） */}
        {showIterationProgress && (
          <div className="mt-2 space-y-1.5">
            <IterationBadge
              current={currentIteration}
              total={totalIterations}
              currentChapter={currentChapter}
            />
            <LoopProgressBar current={currentIteration + 1} total={totalIterations} />
          </div>
        )}

        {/* 循环反馈状态 */}
        <div className="mt-1.5 flex items-center gap-2">
          {nodeData.loop_feedback ? (
            <span className="text-xs text-green-600 bg-green-100/80 px-1.5 py-0.5 rounded flex items-center gap-1">
              <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              反馈
            </span>
          ) : null}
          {step > 1 && (
            <span className="text-xs text-gray-500 bg-gray-100 px-1.5 py-0.5 rounded">
              步长: {step}
            </span>
          )}
        </div>
      </div>

      {/* 子节点内容区域 */}
      <div className="relative p-2 min-h-[120px]">
        {childNodes.length === 0 ? (
          <EmptyChildrenPlaceholder />
        ) : (
          /* 子节点会通过 React Flow 自动渲染 */
          <div className="text-xs text-gray-400 mb-2">
            包含 {childNodes.length} 个节点
          </div>
        )}
      </div>

      {/* 循环容器不需要连接点，外节点直接连接子节点 */}

      {/* 缩放控制 */}
      <ResizeControl />
    </div>
  );
};

LoopNodeComponent.displayName = 'LoopNode';

export const LoopNode = memo(LoopNodeComponent);
export default LoopNode;
